import _ from 'lodash'
import React, { useState, useCallback, useEffect, useRef, useMemo, ChangeEvent } from 'react'
import i18n from 'live/services/i18n'

import globalConfirmation from 'live/services/global-confirmation'

export function useStateRef<T = any>(initialValue: T) {
    const [value, setValue] = useState(initialValue)

    const ref = useRef(value)

    useEffect(() => {
        ref.current = value
    }, [value])

    return [value, setValue, ref]
}

export function usePrevious(value: any) {
    const ref = useRef()
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

export function useFieldsState<T = any>({ formState, persistedValues }: { persistedValues: any; formState: any }) {
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [canEdit] = useState(true)

    const toggleEdit: (value?: boolean) => void = useCallback(
        value => {
            setEditing(editing => (/*canEdit &&*/ value !== undefined ? value : !editing))
        },
        [editing]
    )

    const onCancel = useCallback(() => {
        if (formState.setField) {
            _.map(persistedValues, (v, k) => formState.setField(k, persistedValues[k]))
        } else if (formState.resetFields) {
            formState.resetFields()
        }

        toggleEdit()
    }, [persistedValues])

    const state = useMemo(() => {
        return {
            editing,
            saving,
            toggleEdit,
            setSaving,
            submitted,
            canEdit,
            onCancel
        }
    }, [editing, saving, submitted])

    return [state]
}

interface FieldsMap {
    [key: string]: {
        [field: string]: string
    }
}

export const useFormState = <T = any>(initialState: T[], keyAttribute: keyof T) => {
    const getInitialFieldsMap = () => {
        const getInitialValue = (arg: T) => {
            let initialValue = {}
            for (const [key, value] of Object.entries(arg)) {
                initialValue = {
                    ...initialValue,
                    [key]: value
                }
            }
            return initialValue
        }

        let initialFields: FieldsMap = {}
        for (let arg of initialState) {
            initialFields = {
                ...initialFields,
                [(arg[keyAttribute] as unknown) as keyof FieldsMap]: getInitialValue(arg)
            }
        }
        return initialFields
    }

    const [initialValuesMap] = useState(getInitialFieldsMap())
    const [fieldsMap, setFieldsMap] = useState(initialValuesMap)

    const text = (key: string | number, field: string) => {
        const onChange = (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setFieldsMap({
                ...fieldsMap,
                [key]: {
                    ...fieldsMap[key as keyof FieldsMap],
                    [field]: value
                }
            } as FieldsMap)
        }

        return {
            onChange,
            value: fieldsMap[key][field]
        }
    }

    const resetFields = () => {
        setFieldsMap(initialValuesMap)
    }

    return [
        { values: Object.values(fieldsMap), resetFields },
        {
            text
        }
    ]
}

export function useFieldsetState(skipTabContext = false, canEdit: boolean) {
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const toggleEdit = useCallback(
        value => {
            setEditing(editing => canEdit && (value !== undefined ? value : !editing))
        },
        [editing, canEdit]
    )

    return useMemo(() => {
        return {
            editing,
            saving,
            setSaving,
            submitted,
            setSubmitted,
            toggleEdit,
            canEdit: canEdit || false
        }
    }, [editing, saving, submitted, canEdit, toggleEdit])
}

export function useSheetModalState({ show, onHide }: any) {
    const [hasChanges, setHasChanges] = useState(false)
    const { current: beforeUnload } = useRef((e:any) => (e.returnValue = i18n('Changes will be lost')))

    useEffect(() => {
        window.removeEventListener('beforeunload', beforeUnload)

        if (show && hasChanges) window.addEventListener('beforeunload', beforeUnload)
        else window.removeEventListener('beforeunload', beforeUnload)

        return () => window.removeEventListener('beforeunload', beforeUnload)
    }, [show, hasChanges])

    const onCancel = () => {
        if (!show) return

        if (hasChanges) return globalConfirmation.warn(i18n('Changes will be lost'), onHide, { btnText: 'OK' })

        return onHide()
    }

    return {
        hasChanges,
        setHasChanges,
        onCancel
    }
}
